import { html, id, text, list, attribute } from 'wirejs-dom/v2';
import { marked } from 'marked';
import CardService from '../../services/cards';
import CardSuggestion from '../ui/card-suggestion';
import ProContent, { UpgradeButton } from '../ui/pro-content';
import _Alert from '../ui/alert';
import Button from '../ui/button';
import PruningTips from '../analysis/pruning';
import { ProFeatures } from '../../services';
import ProFeatureSummary from '../ui/pro-feature-summary';
import Tracker from '../navigation/tracker';

const tracker = new Tracker();

/**
 * @typedef {Awaited<Awaited<
 * 	ReturnType<typeof CardService.getRecommendations>
 * >['data']>} Recommendations
 */

/**
 * @typedef {{
 * 	name: string;
 * }} Card
 */

/**
 * Suggestions grouped by tactic and then card. Values are counts of "hits".
 * 
 * ```
 * {
 * 	"board wipe": {
 * 		"End Hostilities": 5,
 * 		"Armageddon": 7,
 * 	}
 * }
 * ```
 * 
 * @typedef {Record<string, Record<string, number>>} SuggestionsByTactics
 */

/**
 * Suggestions grouped by card. Keys are card names. Values contain the tactics the
 * card is predicted to fulfill and a numeric `score` that can be used to rank suggestions
 * relative to each other.
 * 
 * ```
 * {
 * 	"End Hostilities": {
 * 		tactics: { 'Board wipe', 'Disruption' },
 * 		score: 8,
 * 	}
 * }
 * ```
 * 
 * @typedef {Record<string, {
 * 	tactics: Set<string>;
 * 	score: number;
 * }>} SuggestionsByCard
 */

/**
 * @typedef {{
 * 	tactics: Set<string>;
 * 	score: number;
 * 	card: string;
 * }} IndividualSuggestion
 */

/**
 * 
 * @param {Recommendations} data 
 * @returns {SuggestionsByTactics}
 */
const groupByTactics = data => {
	const tactics = {};
	for (const [tacticName, cards] of Object.entries(data)) {
		tactics[tacticName] = {};
		for (const [_cardName, suggestions] of Object.entries(cards)) {
			for (const suggestion of suggestions) {
				const count = tactics[tacticName][suggestion.card.name] || 0;
				tactics[tacticName][suggestion.card.name] = count + 1;
			}
		}
	}
	return tactics;
};

/**
 * 
 * @param {SuggestionsByTactics} byTactic 
 * @returns {SuggestionsByCard}
 */
const groupByCard = byTactic => {
	const cards = {};
	for (const [tactic, tacticCards] of Object.entries(byTactic)) {
		for (const [card, score] of Object.entries(tacticCards)) {
			cards[card] = cards[card] || {
				tactics: new Set(), 
				score: 0,
			};
			cards[card].score += score;
			cards[card].tactics.add(tactic);
		}
	}
	return cards;
};

/**
 * 
 * @param {SuggestionsByCard} byCard 
 * @returns
 */
const cardGroupToArray = byCard => {
	const cards = [];
	for (const [card, data] of Object.entries(byCard)) {
		cards.push({ card, ...data });
	}
	return cards;
};

/**
 * For sorting individual card suggestions by score. E.g.,
 * 
 * ```
 * suggestions.sort(byScore)
 * ```
 * 
 * @param {IndividualSuggestion} a 
 * @param {IndividualSuggestion} b 
 * @returns 
 */
const byScore = (a, b) => {
	if (a.score > b.score) return -1;
	if (a.score < b.score) return 1;
	if (a.tactics.size > b.tactics.size) return -1;
	if (a.tactics.size < b.tactics.size) return 1;
	return 0;
};

/**
 * Creates a stateful function that filters suggestions, attempting to ensure the
 * required tactical buckets (`tactics`) are filled.
 * 
 * Returns no more than `targetTotalCount`, but will return less if there are insufficient
 * items to fill each of the buckets.
 * 
 * @param {string[]} tactics - The list of tactics that need filling.
 * @param {Set<string>} excluding - The list of cards to exclude (e.g., already in the deck).
 * @param {number} [targetCardCount] - The total number of items you ideally need back.
 * @return {(item: IndividualSuggestion) => boolean} - A function used to filter a list.
 */
const bucketFill = (tactics, excluding, targetTotalCount = 100) => {
	const buckets = Object.fromEntries(tactics.map(name => [name, 0]));
	const bucketMaxCount = Math.floor(targetTotalCount / tactics.length);
	let count = 0;

	return (item) => {
		if (count > targetTotalCount) return false;
		if (excluding.has(item.card)) return false;

		let canReturn = false;
		for (const tactic of item.tactics) {
			if (buckets[tactic] < bucketMaxCount) {
				canReturn = true;
				buckets[tactic]++;
			}
		}

		return canReturn;
	};
};

/**
 * 
 * @param {Recommendations} data 
 * @param {Set<string>} excluding
 * @param {number} limit
 * @returns 
 */
const processRecommendations = (data, excluding, limit) => {
	const byTactic = groupByTactics(data);
	const tactics = Object.keys(byTactic);
	const groupedByCard = groupByCard(byTactic);
	return cardGroupToArray(groupedByCard)
		.sort(byScore)
		.filter(bucketFill(tactics, excluding, limit))
	;
};

/**
 * 
 * @param {Recommendations} recommendation 
 * @param {Card} existing 
 * @param {number} targetCardCount 
 * @returns 
 */
const extractNewCards = async (recommendation, existing, targetCardCount) => {
	const existingNames = new Set(existing.map(e => e.name));
	const baseCardList = processRecommendations(
			recommendation,
			existingNames,
			10 + targetCardCount - existingNames.size
		)
		.map(r => ({
			card: r.card,
			quantity: 1,
			tactics: Object.fromEntries([...r.tactics].map(t => [t, true]))
		}));

	const cards = await CardService.getBatch(baseCardList.map(r => r.card));

	return cards.map(c => ({
		name: c.name,
		type: c.type,
		text: c.text,
		cmc: c.cmc,
		manaCost: c.manaCost,
		rarity: c.rarity,
		power: c.power,
		toughness: c.toughness,
		activeQuantity: 1,
		sideboarded: 0,
		tactics: baseCardList.find(b => b.card === c.name).tactics
	}));
}

const mapToCardResults = async (tacticOptions, results, isDeckLocked) => {
	const cards = await CardService.getBatch(results.map(r => r.card));
	return results.map(result => {
		const card = cards.find(c => c.name === result.card);
		const tactics = result.tactics;
		return new CardSuggestion({
			...card,
			tactics,
			tacticOptions,
			isDeckLocked
		});
	});
}

/**
 * @param {undefined | Partial<{
 * 	deck: {
 * 		applyChanges: (arg: {
 * 			card: string;
 * 			quantity: number;
 * 			tactics: Record<string, boolean>;
 * 		}[]) => void;
 * 		isLocked: () => boolean;
 * 	};
 * 	tactics: Array<{
 * 		name: string;
 * 		turnNeeded: number;
 * 		quantityNeeded: number;
 * 		actualQuantity: number;
 * 		actualTurn: number;
 * 		successRate: number;
 * 		keepLower: number;
 * 		keepUpper: number;
 * 		needed: number;
 * 		sample: number;
 * 	}>;
 * 	cards: Array<{
 * 		name: string;
 * 		type: string;
 * 		text: string;
 * 		cmc: number;
 * 		manaCost: string;
 * 		rarity: string;
 * 		power: number | undefined;
 * 		toughness: number | undefined;
 * 		activeQuantity: number;
 * 		sideboarded: number;
 * 		tactics: Record<string, boolean>;
 * 	}>;
 * 	commander: {
 * 		name: string;
 * 		activeQuantity: number;
 * 		sideboarded: number;
 * 		text: string;
 * 		tactics: Record<string, boolean>;
 * 	} | undefined;
 * 	successRate: number;
 * 	mulliganRate: number;
 * 	missRate: number;
 * 	manaCost: {
 * 		cmcs: number[];
 * 		curve: Record<number, number>;
 * 		colors: Record<string, number>;
 * 		median: number;
 * 		total: number;
 * 	};
 * 	supply: Record<number, number>;
 * }>} data
 */
const ProAnalysis = function(data = {}) {
	const self = html`<db:proanalysis>
		<div>
			<p ${id('actionButtons')} style='text-align: center;'>
				${ProContent({
					feature: 'advanced-analysis-tools',
					content: () => html`<span>
						<button onclick=${recommendCards}>Suggest Cards</button>
						<button onclick=${optimize60}>Optimize 60+</button>
						<button onclick=${optimize100}>Optimize 100+</button>
						<button onclick=${autoBuild}>Auto-Build (alpha)</button>
					</span>`,
					upgradeContent: () => html`<span>
						<button disabled title='Upgrade for access.'>Suggest Cards</button>
						<button disabled title='Upgrade for access.'>Optimize 60+</button>
						<button disabled title='Upgrade for access.'>Optimize 100+</button>
						<button disabled title='Upgrade for access.'>Auto-Build (alpha)</button>
					</span>`
				})}
				${ProContent({
					feature: 'core-llm-integration',
					content: () => html`<span>
						<button onclick=${consultAgent}>Consult AI Agent</button>
					</span>`,
					upgradeContent: () => html`<span>
						<button disabled title='Upgrade for access.'>Consult AI Agent</button>
					</span>`
				})}
			</p>
			<p ${id('content')}>Loading ...</p>
			${ProContent({
				feature: ['advanced-analysis-tools', 'core-llm-integration'],
				content: html`<div style='
					margin: 2rem 1rem 1rem;
					text-align: center;
				'></div>`,
				upgradeContent: () => html`<div style='
						border: 1px solid gray;
						border-radius: 0.25rem;
						padding: 1rem;
						padding-top: 0rem;
						text-align: center;
						background-color: honeydew;
						box-shadow: 0.125rem 0.125rem 0.25rem 0rem gray;
					'>
					<div><h3 style='color: rgb(197, 75, 22);'>You're missing out!</h3>
						Our <b>Adavnced Toolkit Owners</b> save time building and optimizing decks by eliminating countless hours of manual searching, sorting, and stabilizing.
					</div>
					<div style='text-align: center; margin: 2rem;'>${UpgradeButton({
						text: "Unlock Advanced Tools"
					})}</div>
					<p>Get the same fast building and optimization tools <b>other players</b> are using.</p>
					${new ProFeatureSummary()}
				</div>`
			})}
		</div>
	</db:proanalysis>`;

	const memos = new Map();

	/**
	 * 
	 * @param {Recommendations} recs 
	 * @param {boolean} isDeckLocked 
	 * @returns 
	 */
	const formatRecommendations = async (recs, isDeckLocked) => {
		const recommendations = processRecommendations(recs, new Set(), 10);
		return mapToCardResults(
			data.tactics,
			recommendations,
			isDeckLocked
		).then(results => {
			let addToDeckInstructions = '';
			let addButton = `(You can't add cards to this deck.)`;
			if (!isDeckLocked) {
				const buttonText = `Add to Deck`;
				addToDeckInstructions = `<p>
					Review the tactics, add a quantities to try,
					and hit the <b>${buttonText}</b> button at the bottom.
				</p>`;
				addButton = new Button({
					text: buttonText,
					onclick: () => {
						tracker.track('mtg_recommended_apply');
						const additions = results
							.filter(r => r.quantity > 0)
							.map(r => ({
								card: r.name,
								quantity: r.quantity,
								tactics: Object.fromEntries([...r.tactics].map(t => [
									t, true
								]))
							}));
						data.deck.applyChanges(additions);
						self.data.content = html`<div>Applying changes ...</div>`;
					}
				})
			}
			return html`<div>
				<h2>Suggested Cards</h2>
				${addToDeckInstructions}
				${results}
				${addButton}
			</div>`;
		});
	};

	/**
	 * 
	 * @param {(() => AsyncGenerator<any>)} f 
	 * @returns {Promise<void>}
	 */
	const modalOperation = async (f) => {
		self.data.actionButtons.style.display = 'none';
		self.data.content = html`<p>Running ... </p>`;
		for await (const output of f()) {
			self.data.content = html`<div>${output}</div>`;
		}
		self.data.actionButtons.style.display = '';
	};

	/**
	 * @param {string?} status
	 */
	const Loader = (status) => {
		return html`<p>${text('status', status ?? "Working on it ...")}
			<progress value="${attribute('progress', 0)}"></progress>
			<b>${text('progress', 0, p => Math.round(p * 100))}%</b>
		</p>`;
	};

	/**
	 * @param {string[]} options
	 */
	const Options = (options) => {
		let selectResult;
		let selectIndexResult;

		/**
		 * @type {Promise<string>}
		 */
		const selection = new Promise(resolve => {
			selectResult = resolve;
		});

		/**
		 * @type {Promise<number>}
		 */
		const selectionIndex = new Promise(resolve => {
			selectIndexResult = resolve;
		});

		return html`<p>
			${options.map((o, i) => html`<button onclick=${() => {
				selectResult(o);
				selectIndexResult(i);
			}}>${o}</button>`)}
		</p>`.extend(() => ({
			data: {
				selection,
				selectionIndex
			}
		}));
	};

	const recommendCards = () => modalOperation(async function * () {
		tracker.track('mtg_recommend_start');
		const loader = Loader("Finding similar cards ... ");
		yield loader;

		const recommendations = await CardService.getRecommendations(data.cards);
		recommendations.onprogress(p => loader.data.progress = p);

		yield formatRecommendations(await recommendations.data, data.deck.isLocked());
	});

	const optimize60 = () => modalOperation(async function * () {
		tracker.track('mtg_optimize_start', {
			mtg_optimize_n: 60
		});
		const tips = new PruningTips();
		yield tips;
		await tips.refresh(data.cards, data.tactics, data.deck, 60);
		yield tips;
	});

	const optimize100 = () => modalOperation(async function * () {
		tracker.track('mtg_optimize_start', {
			mtg_optimize_n: 100
		});
		const tips = new PruningTips();
		yield tips;
		await tips.refresh(data.cards, data.tactics, data.deck, 100);
		yield tips;
	});

	const consultAgent = () => modalOperation(async function * () {
		tracker.track('mtg_agent_consult');
		yield 'Consulting AI Agent ...';
		const mlresult = await ProFeatures.getLLMAnalysis(data);
		if (mlresult?.message?.content) {
			yield marked.parse(mlresult.message.content).toString();
		} else {
			yield "Error fetching AI Analysis. Please try again.";
		}
	});

	const autoBuild = () => modalOperation(async function * () {
		tracker.track('mtg_autobuild_start');
		const options = Options(['Build to 60', 'Build to 100', 'Cancel']);

		if (data.deck.isLocked()) {
			yield html`<div>
				<h3>Auto-Build can't modify someone else's deck!</h3>
				<p>You need to <b>make a copy first.</b></p>
				<p><button onclick=${() => data.deck.createNew([])}>Make a Copy</button></p>
			</div>`;
			return;
		}

		yield html`<div>
			<h3>Auto-Build will modify this deck.</h3>
			<p>If this isn't what you intend, <b>make a copy first.</b></p>
			<p><button onclick=${() => data.deck.createNew([])}>Make a Copy</button></p>
			<p>Otherwise, let's make some Magic (the Gathering) together.</p>
			${options}
			<p><b>NOTE:</b> Auto-build currently depends heavily on your assigned tactics.
				If you find a card or group becomes either flooded or deficient, review the
				assigned tactics and their "schedules" &mdash; the "Needs X by turn Y" portion.
			</p>
		</div>`;

		const selection = await options.data.selectionIndex;
		
		if (selection === 2) {
			tracker.track('mtg_autobuild_cancel');
			yield html`<p></p>`;
			return;
		} else {
			tracker.track('mtg_autobuild_apply');
		}

		const targetCardCount = selection === 0 ? 60 : 100;
		let cards = [...data.cards];

		// pre-balancing
		let pruning = new PruningTips();
		yield `Prebalancing ...`;
		await pruning.refresh(cards, data.tactics, {}, targetCardCount);
		for (const s of Array.prototype.slice.call(pruning.suggestions)) {
			cards.find(c => c.name === s.card).activeQuantity = s.quantity;
		};

		// add cards
		const loader = Loader(`Finding related cards ...`);
		yield loader;
		const recommendationJob = await CardService.getRecommendations(cards);
		recommendationJob.onprogress(p => loader.data.progress = p);
		const additions = await extractNewCards(
			await recommendationJob.data,
			cards,
			targetCardCount
		);
		cards.push(...additions);

		// rebalancing
		pruning = new PruningTips();
		yield `Rebalancing ...`;
		await pruning.refresh(cards, data.tactics, {}, targetCardCount);
		console.log(pruning.suggestions);
		for (const s of Array.prototype.slice.call(pruning.suggestions)) {
			cards.find(c => c.name === s.card).activeQuantity = s.quantity;
		};
		cards = cards.filter(c => c.activeQuantity > 0);

		const counts = {};
		for (const card of cards) {
			counts[card.name] = counts[card.name] ?? 0;
			counts[card.name] += 1;
		}

		data.deck.setCards(cards);

		yield "Done.";
	});

	const refresh = (newData = {}) => {
		memos.clear();
		data = newData;
		if (!data.cards) {
			self.data.content = html`<p>You have a Pro account, but the deck isn't ready for analysis. Make sure it has cards with assigned tactics!</p>`;
			self.data.actionButtons.style.display = 'none';
		} else {
			self.data.actionButtons.style.display = '';
			self.data.content = html`<p>
				<p>🥇 You have a Pro account. Cards are loaded. <i>Buckle up</i>. 🏍️</p>
				<h4>Here's what you're getting with Pro:</h4>
				${new ProFeatureSummary()}
			</p>`;
		}
	};

	return self.extend(self => ({
		refresh(newData) {
			refresh(newData);
		}
	})).onadd(() => {
		refresh();
	});
};

export { ProAnalysis };
